import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Clapperboard, Play, Pause, CheckCircle2, Clock, Users, Plus, LogOut,
  Trophy, BarChart3, Upload, Zap, Star, X, Film, Image, Layers, Video, Sparkles,
  Calendar, CircleDot, Coffee, Monitor, Edit2, LayoutGrid, ArrowRight
} from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from '../components/ui/sheet';
import { toast } from 'sonner';
import SidebarLayout from '../components/SidebarLayout';
import ChangeHistoryButton from '../components/ChangeHistoryButton';
import BigClock from '../components/BigClock';
import EmployeePerformanceTab from '../components/EmployeePerformanceTab';

const PROD_DEPARTMENTS = ['Video & Motion', 'Creative & Design'];

import API from '../utils/api';
import { externalLinkProps } from '../utils/url';

const TYPE_ICONS = {
  'Short-form Video': <Film size={14} className="text-pink-400" />,
  'Long-form Video': <Video size={14} className="text-purple-400" />,
  'Static': <Image size={14} className="text-blue-400" />,
  'Carousel': <Layers size={14} className="text-amber-400" />,
  'Motion Graphic': <Sparkles size={14} className="text-cyan-400" />,
  'Grid': <LayoutGrid size={14} className="text-teal-400" />,
};

const STATUS_COLORS = {
  'Pending': 'bg-slate-500/10 text-slate-400 border-slate-500/20',
  'In Progress': 'bg-cyan-500/10 text-cyan-400 border-cyan-500/20',
  'Paused': 'bg-amber-500/10 text-amber-400 border-amber-500/20',
  'Completed': 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20',
  'Carried Forward': 'bg-indigo-500/10 text-indigo-400 border-indigo-500/20',
};

const PRIORITY_COLORS = {
  'High': 'bg-rose-500/10 text-rose-400',
  'Medium': 'bg-amber-500/10 text-amber-400',
  'Low': 'bg-slate-500/10 text-slate-400',
};

export default function ProductionPage() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const isAdmin = user?.role === 'admin';
  const prodLevel = isAdmin ? 'edit' : (user?.effective_permissions?.production || 'none');
  const isProdTeam = prodLevel === 'edit' && ['Video & Motion', 'Creative & Design'].includes(user?.department);
  // Capability flags (clean mental model):
  //   view       → see everything, no buttons
  //   task       → view + allocate/edit tasks (no execute)
  //   edit       → full
  const canAllocate = ['task_only', 'edit'].includes(prodLevel);           // New Task, CSV Upload, Edit metadata
  const canExecute = prodLevel === 'edit';                                  // Start/Pause/Complete/Carry-forward
  const isFullView = !isProdTeam;                                           // All Tasks filter (not "My Tasks")
  const canWrite = canAllocate;                                             // kept for backwards-compat in child props
  const [tasks, setTasks] = useState([]);
  const [liveTasks, setLiveTasks] = useState([]);
  const [summary, setSummary] = useState([]);
  const [stats, setStats] = useState(null);
  const [leaderboard, setLeaderboard] = useState([]);
  const [clients, setClients] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [showCSV, setShowCSV] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [tab, setTab] = useState('tasks'); // tasks | live | leaderboard | output
  const [fClient, setFClient] = useState('');
  const [fStatus, setFStatus] = useState('');
  const [fEmployee, setFEmployee] = useState('');
  const [fType, setFType] = useState('');
  const [statsPeriod, setStatsPeriod] = useState('daily');
  const [analyticsData, setAnalyticsData] = useState(null);
  const [analyticsPeriod, setAnalyticsPeriod] = useState('daily');
  const [analyticsCustomStart, setAnalyticsCustomStart] = useState('');
  const [analyticsCustomEnd, setAnalyticsCustomEnd] = useState('');
  const [liveDate, setLiveDate] = useState(() => new Date().toISOString().split('T')[0]);
  const [lbPeriod, setLbPeriod] = useState('monthly');
  const [lbCustomStart, setLbCustomStart] = useState('');
  const [lbCustomEnd, setLbCustomEnd] = useState('');
  const [tasksDate, setTasksDate] = useState(() => new Date().toISOString().split('T')[0]);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const params = {};
      if (fClient && fClient !== 'all') params.client_id = fClient;
      if (fStatus && fStatus !== 'all') params.status = fStatus;
      if (fEmployee && fEmployee !== 'all') params.assigned_to = fEmployee;
      if (fType && fType !== 'all') params.task_type = fType;
      if (tasksDate) params.task_date = tasksDate;

      const lbParams = { period: lbPeriod };
      if (lbPeriod === 'custom' && lbCustomStart && lbCustomEnd) {
        lbParams.start_date = lbCustomStart;
        lbParams.end_date = lbCustomEnd;
      }

      const analyticsParams = { period: analyticsPeriod };
      if (analyticsPeriod === 'custom' && analyticsCustomStart && analyticsCustomEnd) {
        analyticsParams.start_date = analyticsCustomStart;
        analyticsParams.end_date = analyticsCustomEnd;
      }

      const calls = [
        axios.get(`${API}/production/tasks`, { withCredentials: true, params }),
        axios.get(`${API}/production/stats?period=${statsPeriod}`, { withCredentials: true }),
        axios.get(`${API}/production/live`, { withCredentials: true }),
        axios.get(`${API}/production/leaderboard`, { withCredentials: true, params: lbParams }),
        axios.get(`${API}/production/summary?date=${liveDate}`, { withCredentials: true }),
        axios.get(`${API}/production/analytics`, { withCredentials: true, params: analyticsParams }),
      ];
      if (isAdmin || isFullView) {
        calls.push(axios.get(`${API}/clients`, { withCredentials: true }));
        calls.push(axios.get(`${API}/employees`, { withCredentials: true }));
      }
      const results = await Promise.all(calls);
      setTasks(results[0].data);
      setStats(results[1].data);
      setLiveTasks(results[2].data);
      setLeaderboard(results[3].data);
      setSummary(results[4].data);
      setAnalyticsData(results[5].data);
      if (isAdmin || isFullView) {
        setClients(results[6].data);
        setEmployees(results[7].data.filter(e => e.status === 'Active' && PROD_DEPARTMENTS.includes(e.department)));
      }
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  }, [fClient, fStatus, fEmployee, fType, statsPeriod, isAdmin, isFullView, liveDate, lbPeriod, lbCustomStart, lbCustomEnd, tasksDate, analyticsPeriod, analyticsCustomStart, analyticsCustomEnd]);

  useEffect(() => { fetchData(); }, [fetchData]);

  // Live refresh every 30s
  useEffect(() => {
    const iv = setInterval(() => {
      axios.get(`${API}/production/live`, { withCredentials: true }).then(r => setLiveTasks(r.data)).catch(() => {});
    }, 30000);
    return () => clearInterval(iv);
  }, []);

  const handleLogout = async () => { await logout(); navigate('/login'); };

  const handleAction = async (taskId, action, extraData) => {
    try {
      if (action === 'start' || action === 'pause') {
        await axios.post(`${API}/production/tasks/${taskId}/${action}`, {}, { withCredentials: true });
        toast.success(action === 'start' ? 'Task started' : 'Task paused');
      } else if (action === 'complete') {
        await axios.post(`${API}/production/tasks/${taskId}/complete`, extraData, { withCredentials: true });
        toast.success('Task completed — sent for approval');
      }
      fetchData();
    } catch (err) { toast.error(err.response?.data?.detail || 'Action failed'); }
  };

  const handleToggleSpecial = async (taskId) => {
    try {
      await axios.put(`${API}/production/tasks/${taskId}/special`, {}, { withCredentials: true });
      toast.success('Special production updated');
      fetchData();
    } catch (err) { toast.error('Failed'); }
  };

  if (loading && !stats) {
    return <SidebarLayout title="Production" subtitle="Production Management"><div className="flex items-center justify-center py-20"><div className="text-white">Loading...</div></div></SidebarLayout>;
  }

  return (
    <SidebarLayout title="Production Board" subtitle="Production Management">
        {/* KPI Cards */}
        <div className="kpi-scroll md:grid-cols-4 gap-4 mb-4" data-testid="production-kpis">
          <KPI icon={<Clapperboard className="text-purple-400" />} label="Today's Tasks" value={tasks.length} color="purple" />
          <KPI icon={<CheckCircle2 className="text-emerald-400" />} label="Completed" value={tasks.filter(t => t.status === 'Completed').length} color="emerald" />
          <KPI icon={<Play className="text-cyan-400" />} label="Live" value={liveTasks.length} color="cyan" />
          <KPI icon={<Clock className="text-amber-400" />} label="In Progress" value={tasks.filter(t => t.status === 'In Progress').length} color="amber" />
        </div>

        {/* Big Clock */}
        <BigClock />

        {/* Tab Nav — view users see all tabs read-only, task_only users see all tabs (just no write buttons) */}
        <div className="flex items-center gap-2 mb-6 mt-6 overflow-x-auto">
          {[...['tasks', 'leaderboard', 'output'], ...(isAdmin ? ['review'] : [])].map(t => (
            <button key={t} onClick={() => setTab(t)} className={`px-4 py-2 rounded-lg text-xs font-semibold tracking-wide uppercase transition-colors whitespace-nowrap ${tab === t ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 hover:text-white border border-white/10'}`} data-testid={`tab-${t}`}>
              {t === 'tasks' ? (isFullView ? 'All Tasks' : 'My Tasks') : t === 'leaderboard' ? 'Leaderboard' : t === 'output' ? 'Output Stats' : 'Performance Review'}
            </button>
          ))}
          <div className="flex-1" />
          <Button onClick={() => window.open('/production/cast', '_blank')} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="live-mode-btn"><Monitor size={14} className="mr-1" /> Live Mode</Button>
          {canAllocate && (
            <>
              <Button onClick={() => setShowCSV(true)} variant="outline" className="bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="csv-upload-btn"><Upload size={14} className="mr-1" /> CSV Upload</Button>
              <Button onClick={() => setShowCreate(true)} className="bg-indigo-600 hover:bg-indigo-700 shadow-[0_0_15px_rgba(79,70,229,0.3)] h-9 text-xs" data-testid="create-task-btn"><Plus size={14} className="mr-1" /> New Task</Button>
            </>
          )}
        </div>

        {/* Tab Content */}
        {tab === 'tasks' && (
          <TasksTab tasks={tasks} canWrite={canWrite} canExecute={canExecute} isAdmin={isAdmin} isFullView={isFullView} clients={clients} employees={employees}
            fClient={fClient} setFClient={setFClient} fStatus={fStatus} setFStatus={setFStatus}
            fEmployee={fEmployee} setFEmployee={setFEmployee} fType={fType} setFType={setFType}
            tasksDate={tasksDate} setTasksDate={setTasksDate} user={user}
            onAction={handleAction} onSelect={setSelectedTask} onToggleSpecial={handleToggleSpecial} />
        )}
        {tab === 'leaderboard' && <LeaderboardTab leaderboard={leaderboard} period={lbPeriod} setPeriod={setLbPeriod} customStart={lbCustomStart} setCustomStart={setLbCustomStart} customEnd={lbCustomEnd} setCustomEnd={setLbCustomEnd} />}
        {tab === 'output' && <OutputTab analytics={analyticsData} period={analyticsPeriod} setPeriod={setAnalyticsPeriod} customStart={analyticsCustomStart} setCustomStart={setAnalyticsCustomStart} customEnd={analyticsCustomEnd} setCustomEnd={setAnalyticsCustomEnd} />}
        {tab === 'review' && <EmployeePerformanceTab board="production" />}

      {showCreate && <CreateTaskDialog clients={clients} employees={employees} onClose={() => setShowCreate(false)} onCreated={() => { setShowCreate(false); fetchData(); }} />}
      {showCSV && <CSVUploadDialog clients={clients} employees={employees} onClose={() => setShowCSV(false)} onDone={() => { setShowCSV(false); fetchData(); }} />}
      {selectedTask && <TaskDetailSheet task={selectedTask} canWrite={canWrite} canExecute={canExecute} isAdmin={isAdmin} user={user} onClose={() => setSelectedTask(null)} onAction={handleAction} onToggleSpecial={handleToggleSpecial} onUpdate={() => { setSelectedTask(null); fetchData(); }} />}
    </SidebarLayout>
  );
}

function KPI({ icon, label, value, sub, color }) {
  const borders = { purple: 'border-purple-500/20', cyan: 'border-cyan-500/20', emerald: 'border-emerald-500/20', amber: 'border-amber-500/20' };
  return (
    <div className={`kpi-card border-l-4 ${borders[color]}`}>
      <div className="flex items-start justify-between mb-2">
        <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{label}</span>{icon}
      </div>
      <div className="font-heading text-3xl font-black tracking-tight text-white">{value}</div>
      {sub && <span className="text-[10px] text-slate-500 capitalize">{sub}</span>}
    </div>
  );
}

// ========== TASKS TAB ==========
function TasksTab({ tasks, canWrite, canExecute, isAdmin, isFullView, clients, employees, fClient, setFClient, fStatus, setFStatus, fEmployee, setFEmployee, fType, setFType, tasksDate, setTasksDate, user, onAction, onSelect, onToggleSpecial }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = tasksDate === todayStr;
  const isOwner = (t) => isAdmin || t.assigned_to === user?.employee_id;

  // Compute per-employee progress
  const empProgress = useMemo(() => {
    const map = {};
    tasks.forEach(t => {
      const eid = t.assigned_to;
      if (!map[eid]) map[eid] = { total: 0, done: 0 };
      map[eid].total++;
      if (t.status === 'Completed') map[eid].done++;
    });
    return map;
  }, [tasks]);

  return (
    <div className="animate-fadeIn">
      {/* Date filter row */}
      <div className="flex items-center gap-3 mb-4">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">Date:</span>
        <input
          type="date"
          value={tasksDate}
          onChange={(e) => setTasksDate(e.target.value)}
          className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          data-testid="tasks-date-filter"
        />
        {!isToday && (
          <button
            onClick={() => setTasksDate(todayStr)}
            className="text-[10px] px-2 py-1 rounded bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors"
            data-testid="tasks-date-today-btn"
          >
            Back to Today
          </button>
        )}
        <span className="text-[10px] text-slate-500">{isToday ? "Showing today's tasks" : `Showing tasks for ${tasksDate}`}</span>
      </div>

      {isFullView && (
        <div className="flex gap-3 mb-4 flex-wrap">
          <Select value={fClient} onValueChange={setFClient}>
            <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-client"><SelectValue placeholder="Client" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]"><SelectItem value="all">All Clients</SelectItem>{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fEmployee} onValueChange={setFEmployee}>
            <SelectTrigger className="w-[150px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-employee"><SelectValue placeholder="Employee" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]"><SelectItem value="all">All</SelectItem>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
          </Select>
          <Select value={fStatus} onValueChange={setFStatus}>
            <SelectTrigger className="w-[130px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-status"><SelectValue placeholder="Status" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="all">All</SelectItem><SelectItem value="Pending">Pending</SelectItem><SelectItem value="In Progress">In Progress</SelectItem><SelectItem value="Paused">Paused</SelectItem><SelectItem value="Completed">Completed</SelectItem><SelectItem value="Carried Forward">Carried Forward</SelectItem></SelectContent>
          </Select>
          <Select value={fType} onValueChange={setFType}>
            <SelectTrigger className="w-[160px] bg-black/40 border-white/10 text-slate-300 h-9 text-xs" data-testid="filter-type"><SelectValue placeholder="Task Type" /></SelectTrigger>
            <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="all">All Types</SelectItem><SelectItem value="Short-form Video">Short-form Video</SelectItem><SelectItem value="Long-form Video">Long-form Video</SelectItem><SelectItem value="Static">Static</SelectItem><SelectItem value="Carousel">Carousel</SelectItem><SelectItem value="Motion Graphic">Motion Graphic</SelectItem><SelectItem value="Grid">Grid</SelectItem></SelectContent>
          </Select>
        </div>
      )}
      <div className="glass-card overflow-hidden" data-testid="tasks-table">
        <div className="overflow-x-auto mobile-cards">
        <table className="data-table">
          <thead><tr>
            <th>Client</th><th>Assigned</th><th>Date</th><th>Type</th><th>Priority</th><th>Status</th><th>Time</th><th>Actions</th>
          </tr></thead>
          <tbody>
            {tasks.length === 0 ? (
              <tr><td colSpan={8} className="text-center py-12 text-slate-500">No tasks found</td></tr>
            ) : tasks.map(t => (
              <tr key={t.task_id} className="cursor-pointer" data-testid={`task-row-${t.task_id}`}>
                <td onClick={() => onSelect(t)} className="text-white text-sm">
                  {t.client_name}
                  {t.special_production && <Star size={12} className="text-amber-400 ml-1 inline" />}
                </td>
                <td onClick={() => onSelect(t)} className="text-slate-300 text-sm">
                  <span>{t.assigned_name}</span>
                  {empProgress[t.assigned_to] && (
                    <span className="ml-2 text-[9px] px-1.5 py-0.5 rounded bg-white/5 text-slate-500 font-mono">
                      {empProgress[t.assigned_to].done}/{empProgress[t.assigned_to].total}
                    </span>
                  )}
                </td>
                <td onClick={() => onSelect(t)} className="text-xs text-slate-400">{t.task_date || '—'}</td>
                <td onClick={() => onSelect(t)}><span className="flex items-center gap-1 text-xs">{TYPE_ICONS[t.task_type]}{t.task_type}</span></td>
                <td onClick={() => onSelect(t)}>{t.priority && <span className={`text-[10px] px-2 py-0.5 rounded ${PRIORITY_COLORS[t.priority]}`}>{t.priority}</span>}</td>
                <td onClick={() => onSelect(t)}><span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[t.status]}`}>{t.status}</span></td>
                <td onClick={() => onSelect(t)} className="text-xs text-slate-400">{t.total_time}</td>
                <td>
                  <div className="flex gap-1">
                    {isAdmin && <ChangeHistoryButton resource="production" recordId={t.task_id} />}
                    {canExecute && isOwner(t) && (t.status === 'Pending' || t.status === 'Paused') && (
                      <button onClick={() => onAction(t.task_id, 'start')} className="p-1.5 rounded bg-emerald-500/10 hover:bg-emerald-500/20 text-emerald-400" data-testid={`start-${t.task_id}`}><Play size={12} /></button>
                    )}
                    {canExecute && isOwner(t) && t.status === 'In Progress' && (
                      <button onClick={() => onAction(t.task_id, 'pause')} className="p-1.5 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-400" data-testid={`pause-${t.task_id}`}><Pause size={12} /></button>
                    )}
                    {t.status === 'Completed' && isAdmin && (
                      <button onClick={() => onToggleSpecial(t.task_id)} className={`p-1.5 rounded ${t.special_production ? 'bg-amber-500/20 text-amber-400' : 'bg-white/5 text-slate-500 hover:text-amber-400'}`} data-testid={`special-${t.task_id}`}><Star size={12} /></button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      </div>
    </div>
  );
}

// ========== LIVE DASHBOARD ==========
function LiveTab({ liveTasks, summary, liveDate, setLiveDate, onAction, onSelect }) {
  const todayStr = new Date().toISOString().split('T')[0];
  const isToday = liveDate === todayStr;

  return (
    <div className="animate-fadeIn" data-testid="live-dashboard">
      {/* Big Clock */}
      <BigClock />

      {/* Date Filter */}
      <div className="flex items-center gap-3 mt-6 mb-4">
        <Calendar size={14} className="text-slate-400" />
        <span className="text-xs text-slate-400 font-semibold uppercase tracking-widest">Date:</span>
        <input
          type="date"
          value={liveDate}
          onChange={(e) => setLiveDate(e.target.value)}
          className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-3 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
          data-testid="live-date-filter"
        />
        {!isToday && (
          <button
            onClick={() => setLiveDate(todayStr)}
            className="text-[10px] px-2 py-1 rounded bg-indigo-600/20 text-indigo-400 hover:bg-indigo-600/30 transition-colors"
            data-testid="live-date-today-btn"
          >
            Back to Today
          </button>
        )}
      </div>

      {/* Employee Summary Table */}
      <div className="glass-card overflow-hidden" data-testid="live-summary">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
          <Users size={14} className="text-indigo-400" />
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">Team Summary</span>
          <span className="text-[10px] text-slate-500 ml-1">({isToday ? 'Today' : liveDate})</span>
          <span className="text-xs text-slate-500 ml-auto">{summary.length} members</span>
        </div>
        <table className="data-table">
          <thead>
            <tr>
              <th>Employee</th>
              <th>Department</th>
              <th>Total Tasks</th>
              <th>Completed</th>
              <th>Status</th>
              <th>Working Since</th>
            </tr>
          </thead>
          <tbody>
            {summary.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500">No production team members found</td></tr>
            ) : summary.map(emp => (
              <tr key={emp.employee_id} data-testid={`summary-${emp.employee_id}`}>
                <td className="text-white text-sm font-medium">{emp.name}</td>
                <td>
                  <span className={`text-[10px] px-2 py-0.5 rounded ${emp.department === 'Video & Motion' ? 'bg-purple-500/10 text-purple-400 border border-purple-500/20' : 'bg-cyan-500/10 text-cyan-400 border border-cyan-500/20'}`}>
                    {emp.department}
                  </span>
                </td>
                <td className="text-slate-300 text-sm font-mono">{emp.total_tasks}</td>
                <td className="text-emerald-400 text-sm font-mono font-bold">{emp.completed}</td>
                <td>
                  {emp.status === 'Working' ? (
                    <span className="flex items-center gap-1.5 text-xs">
                      <CircleDot size={10} className="text-emerald-400 animate-pulse" />
                      <span className="text-emerald-400 font-semibold">Working</span>
                    </span>
                  ) : (
                    <span className="flex items-center gap-1.5 text-xs">
                      <Coffee size={10} className="text-slate-500" />
                      <span className="text-slate-500">Idle</span>
                    </span>
                  )}
                </td>
                <td className="text-xs text-slate-400 font-mono">
                  {emp.working_since ? new Date(emp.working_since).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '—'}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Active Now */}
      <div className="glass-card overflow-hidden mt-6">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
          <span className="text-xs font-semibold tracking-widest uppercase text-emerald-400">Live — In Progress</span>
          <span className="text-xs text-slate-500 ml-auto">{liveTasks.length} active</span>
        </div>
        <table className="data-table">
          <thead><tr><th>Employee</th><th>Task Type</th><th>Client</th><th>Running Since</th><th>Duration</th><th>Action</th></tr></thead>
          <tbody>
            {liveTasks.length === 0 ? (
              <tr><td colSpan={6} className="text-center py-8 text-slate-500">No tasks running right now</td></tr>
            ) : liveTasks.map(t => (
              <tr key={t.task_id} data-testid={`live-${t.task_id}`}>
                <td className="text-white text-sm font-medium">{t.assigned_name}</td>
                <td><span className="flex items-center gap-1 text-xs">{TYPE_ICONS[t.task_type]}{t.task_type}</span></td>
                <td className="text-slate-300 text-sm">{t.client_name}</td>
                <td className="text-xs text-slate-400">{t.running_since ? new Date(t.running_since).toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' }) : '-'}</td>
                <td className="text-xs text-cyan-400 font-mono"><LiveTimer since={t.running_since} totalSecs={t.total_time_secs} previousSecs={t.previous_secs} /></td>
                <td>
                  <button onClick={() => onAction(t.task_id, 'pause')} className="p-1.5 rounded bg-amber-500/10 hover:bg-amber-500/20 text-amber-400" data-testid={`live-pause-${t.task_id}`}><Pause size={12} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function LiveTimer({ since, totalSecs, previousSecs }) {
  const [display, setDisplay] = useState('');
  useEffect(() => {
    const update = () => {
      const prevSecs = previousSecs || 0;
      if (!since) {
        // Paused — show accumulated time from completed sessions
        setDisplay(formatSecs(totalSecs || 0));
        return;
      }
      // Running — previous completed sessions + live current session
      const runStart = new Date(since).getTime();
      const currentSecs = Math.floor((Date.now() - runStart) / 1000);
      setDisplay(formatSecs(prevSecs + currentSecs));
    };
    update();
    const iv = setInterval(update, 1000);
    return () => clearInterval(iv);
  }, [since, totalSecs, previousSecs]);

  return <span>{display}</span>;
}

function formatSecs(s) {
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const sec = s % 60;
  return `${h}h ${m}m ${sec}s`;
}

// ========== LEADERBOARD ==========
function LeaderboardTab({ leaderboard, period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd }) {
  const periodLabels = { daily: 'Today', weekly: 'This Week', monthly: 'This Month', custom: 'Custom Range' };

  return (
    <div className="animate-fadeIn" data-testid="leaderboard">
      {/* Period Selector */}
      <div className="flex items-center gap-2 mb-4 flex-wrap">
        {['daily', 'weekly', 'monthly', 'custom'].map(p => (
          <button
            key={p}
            onClick={() => setPeriod(p)}
            className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white'}`}
            data-testid={`lb-period-${p}`}
          >
            {p === 'daily' ? 'Daily' : p === 'weekly' ? 'Weekly' : p === 'monthly' ? 'Monthly' : 'Custom'}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input
              type="date"
              value={customStart}
              onChange={(e) => setCustomStart(e.target.value)}
              className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              data-testid="lb-custom-start"
            />
            <span className="text-slate-500 text-xs">to</span>
            <input
              type="date"
              value={customEnd}
              onChange={(e) => setCustomEnd(e.target.value)}
              className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500"
              data-testid="lb-custom-end"
            />
          </div>
        )}
      </div>

      <div className="glass-card overflow-hidden">
        <div className="px-4 py-3 border-b border-white/5 flex items-center gap-2">
          <Trophy size={16} className="text-amber-400" />
          <span className="text-xs font-semibold tracking-widest uppercase text-slate-400">{periodLabels[period]} Leaderboard</span>
        </div>
        <table className="data-table">
          <thead><tr><th>Rank</th><th>Employee</th><th>Output Score</th><th>Tasks Completed</th></tr></thead>
          <tbody>
            {leaderboard.length === 0 ? (
              <tr><td colSpan={4} className="text-center py-12 text-slate-500">No completed tasks for this period</td></tr>
            ) : leaderboard.map(e => (
              <tr key={e.employee_id} data-testid={`leader-${e.employee_id}`}>
                <td>
                  <span className={`text-lg font-heading font-black ${e.rank === 1 ? 'text-amber-400' : e.rank === 2 ? 'text-slate-300' : e.rank === 3 ? 'text-amber-600' : 'text-slate-500'}`}>{e.rank}</span>
                </td>
                <td className="text-white font-medium">{e.name}</td>
                <td className="font-heading font-bold text-cyan-400">{e.score.toFixed(1)}</td>
                <td className="text-slate-300">{e.tasks}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ========== OUTPUT STATS (ANALYTICS) ==========
function OutputTab({ analytics, period, setPeriod, customStart, setCustomStart, customEnd, setCustomEnd }) {
  const a = analytics;
  const types = ['Short-form Video', 'Long-form Video', 'Static', 'Carousel', 'Motion Graphic', 'Grid'];
  const periodLabels = { daily: 'Today', weekly: 'This Week', monthly: 'This Month', custom: 'Custom' };

  const fmtTime = (secs) => {
    if (!secs) return '—';
    const h = Math.floor(secs / 3600);
    const m = Math.floor((secs % 3600) / 60);
    if (h > 0) return `${h}h ${m}m`;
    return `${m}m`;
  };

  const maxTypeCreated = a ? Math.max(...types.map(t => a.by_type?.[t]?.created || 0), 1) : 1;

  return (
    <div className="animate-fadeIn space-y-6" data-testid="output-stats">
      {/* Period Selector */}
      <div className="flex items-center gap-2 flex-wrap">
        {['daily', 'weekly', 'monthly', 'custom'].map(p => (
          <button key={p} onClick={() => setPeriod(p)} className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors ${period === p ? 'bg-indigo-600 text-white' : 'bg-white/5 text-slate-400 border border-white/10 hover:text-white'}`} data-testid={`analytics-period-${p}`}>
            {periodLabels[p]}
          </button>
        ))}
        {period === 'custom' && (
          <div className="flex items-center gap-2 ml-2">
            <input type="date" value={customStart} onChange={(e) => setCustomStart(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
            <span className="text-slate-500 text-xs">to</span>
            <input type="date" value={customEnd} onChange={(e) => setCustomEnd(e.target.value)} className="bg-black/40 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-indigo-500" />
          </div>
        )}
        {a && <span className="text-[10px] text-slate-500 ml-auto">{a.date_start === a.date_end ? a.date_start : `${a.date_start} — ${a.date_end}`}</span>}
      </div>

      {!a ? (
        <div className="text-center py-16 text-slate-500">Loading analytics...</div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Created', value: a.total_created, color: 'text-white', bg: 'bg-white/5' },
              { label: 'Completed', value: a.total_completed, color: 'text-emerald-400', bg: 'bg-emerald-500/5' },
              { label: 'Pending', value: a.total_pending, color: 'text-amber-400', bg: 'bg-amber-500/5' },
              { label: 'Fulfillment', value: `${a.fulfillment_pct}%`, color: a.fulfillment_pct >= 80 ? 'text-emerald-400' : a.fulfillment_pct >= 50 ? 'text-amber-400' : 'text-rose-400', bg: 'bg-white/5' },
              { label: 'Avg Time', value: a.avg_completion_time || '—', color: 'text-cyan-400', bg: 'bg-cyan-500/5' },
              { label: 'Total Score', value: a.total_value, color: 'text-purple-400', bg: 'bg-purple-500/5' },
            ].map(k => (
              <div key={k.label} className={`${k.bg} border border-white/5 rounded-xl p-4`} data-testid={`kpi-${k.label.toLowerCase().replace(' ', '-')}`}>
                <div className="text-[10px] text-slate-500 uppercase tracking-widest mb-1">{k.label}</div>
                <div className={`text-2xl font-heading font-black ${k.color}`}>{k.value}</div>
              </div>
            ))}
          </div>

          {/* Content-type focused KPIs — Statics / Carousels / Videos */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="content-type-kpis">
            {(() => {
              const statics = a.by_type?.Static || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
              const carousels = (() => {
                const c = a.by_type?.Carousel || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
                const g = a.by_type?.Grid || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
                return {
                  created: c.created + g.created,
                  completed: c.completed + g.completed,
                  avg_time_secs: Math.round(((c.avg_time_secs * c.completed + g.avg_time_secs * g.completed) / Math.max(1, c.completed + g.completed)) || 0),
                  value: c.value + g.value,
                };
              })();
              const videos = (() => {
                const sv = a.by_type?.['Short-form Video'] || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
                const lv = a.by_type?.['Long-form Video'] || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
                const mg = a.by_type?.['Motion Graphic'] || { created: 0, completed: 0, avg_time_secs: 0, value: 0 };
                const completedTot = sv.completed + lv.completed + mg.completed;
                return {
                  created: sv.created + lv.created + mg.created,
                  completed: completedTot,
                  avg_time_secs: completedTot ? Math.round((sv.avg_time_secs * sv.completed + lv.avg_time_secs * lv.completed + mg.avg_time_secs * mg.completed) / completedTot) : 0,
                  value: sv.value + lv.value + mg.value,
                };
              })();
              const blocks = [
                { key: 'static', label: 'Statics', emoji: '🖼️', icon: <Image size={18} className="text-blue-400" />, accent: 'blue', d: statics },
                { key: 'carousel', label: 'Carousels', emoji: '🎠', icon: <Layers size={18} className="text-amber-400" />, accent: 'amber', d: carousels },
                { key: 'video', label: 'Videos', emoji: '🎬', icon: <BarChart3 size={18} className="text-purple-400" />, accent: 'purple', d: videos },
              ];
              return blocks.map(b => (
                <div key={b.key} className={`glass-card p-4 border-${b.accent}-500/10`} data-testid={`content-kpi-${b.key}`}>
                  <div className="flex items-center justify-between mb-2">
                    <span className="text-[11px] uppercase tracking-widest font-bold text-slate-300 flex items-center gap-2">{b.icon} {b.label}</span>
                    <span className={`text-[10px] px-2 py-0.5 rounded bg-${b.accent}-500/10 text-${b.accent}-400`}>
                      {b.d.created > 0 ? `${Math.round(b.d.completed / b.d.created * 100)}% done` : '0%'}
                    </span>
                  </div>
                  <div className="grid grid-cols-3 gap-2 mt-2">
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase tracking-widest">Created</div>
                      <div className={`text-xl font-heading font-black text-${b.accent}-400`}>{b.d.created}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase tracking-widest">Done</div>
                      <div className="text-xl font-heading font-black text-emerald-400">{b.d.completed}</div>
                    </div>
                    <div>
                      <div className="text-[9px] text-slate-500 uppercase tracking-widest">Avg Time</div>
                      <div className="text-xs font-mono text-cyan-400 leading-tight pt-1">{fmtTime(b.d.avg_time_secs)}</div>
                    </div>
                  </div>
                </div>
              ));
            })()}
          </div>

          {/* Two-Column: By Type + By Client */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* By Type */}
            <div className="glass-card p-5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4 flex items-center gap-2"><BarChart3 size={14} className="text-purple-400" /> Task Type Breakdown</h3>
              <div className="space-y-3">
                {types.map(type => {
                  const d = a.by_type?.[type] || {};
                  const pct = maxTypeCreated > 0 ? (d.created || 0) / maxTypeCreated * 100 : 0;
                  return (
                    <div key={type}>
                      <div className="flex items-center gap-2 mb-1">
                        <span className="text-xs text-slate-400 flex items-center gap-1.5 w-36">{TYPE_ICONS[type]}{type}</span>
                        <span className="ml-auto text-[10px] text-slate-500">{d.completed || 0}/{d.created || 0} done</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <div className="flex-1 h-5 bg-white/5 rounded overflow-hidden">
                          <div className="h-full bg-gradient-to-r from-indigo-600 to-purple-600 rounded transition-all duration-500 flex items-center justify-end pr-1" style={{ width: `${Math.max(pct, 2)}%` }}>
                            {(d.created || 0) > 0 && <span className="text-[9px] text-white/80 font-bold">{d.created}</span>}
                          </div>
                        </div>
                        <span className="text-[10px] text-cyan-400 w-14 text-right font-mono">{fmtTime(d.avg_time_secs)}</span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            {/* By Client */}
            <div className="glass-card p-5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-4 flex items-center gap-2"><Users size={14} className="text-cyan-400" /> Production by Client</h3>
              {a.by_client?.length === 0 ? (
                <div className="text-center py-8 text-slate-500 text-xs">No tasks in this period</div>
              ) : (
                <div className="space-y-2 max-h-[280px] overflow-y-auto">
                  {a.by_client?.map(c => (
                    <div key={c.client_id} className="flex items-center justify-between py-2 px-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div>
                        <div className="text-sm text-white font-medium">{c.name}</div>
                        <div className="text-[10px] text-slate-500">{c.completed}/{c.created} completed</div>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-bold text-purple-400">{c.score.toFixed(1)}</div>
                        <div className="text-[10px] text-slate-500">score</div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* By Employee Table */}
          <div className="glass-card overflow-hidden">
            <div className="px-4 py-3 border-b border-white/5">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 flex items-center gap-2"><Trophy size={14} className="text-amber-400" /> Production by Employee</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="data-table text-xs">
                <thead>
                  <tr>
                    <th>Employee</th>
                    <th className="text-center">Created</th>
                    <th className="text-center">Completed</th>
                    <th className="text-center">Fulfillment</th>
                    <th className="text-center">Time Worked</th>
                    <th className="text-center">Avg / Task</th>
                    <th className="text-center">Score</th>
                  </tr>
                </thead>
                <tbody>
                  {a.by_employee?.length === 0 ? (
                    <tr><td colSpan={7} className="text-center py-8 text-slate-500">No data</td></tr>
                  ) : a.by_employee?.map(e => (
                    <tr key={e.employee_id} data-testid={`emp-analytics-${e.employee_id}`}>
                      <td className="text-white font-medium">{e.name}</td>
                      <td className="text-center text-slate-300">{e.created}</td>
                      <td className="text-center text-emerald-400 font-bold">{e.completed}</td>
                      <td className="text-center">
                        <span className={`text-xs font-semibold px-2 py-0.5 rounded ${e.fulfillment_pct >= 80 ? 'bg-emerald-500/10 text-emerald-400' : e.fulfillment_pct >= 50 ? 'bg-amber-500/10 text-amber-400' : 'bg-rose-500/10 text-rose-400'}`}>
                          {e.fulfillment_pct}%
                        </span>
                      </td>
                      <td className="text-center text-cyan-400 font-mono">{e.total_time || '—'}</td>
                      <td className="text-center text-slate-400 font-mono">{e.avg_time || '—'}</td>
                      <td className="text-center font-heading font-bold text-purple-400">{e.score}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

// ========== CREATE TASK DIALOG ==========
function CreateTaskDialog({ clients, employees, onClose, onCreated }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({ client_id: '', assigned_to: '', task_type: '', task_date: new Date().toISOString().split('T')[0], priority: '', raw_file_link: '', script: '', reference_link: '', notes: '' });
  const set = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    setError(''); setLoading(true);
    try {
      const payload = { ...form };
      if (!payload.priority) delete payload.priority;
      await axios.post(`${API}/production/tasks`, payload, { withCredentials: true });
      toast.success('Task created');
      onCreated();
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(Array.isArray(d) ? d.map(e => e.msg).join(', ') : d || 'Failed');
    } finally { setLoading(false); }
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-lg bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">Create Production Task</DialogTitle></DialogHeader>
        {error && <div className="p-2 rounded bg-rose-500/10 border border-rose-500/20 text-rose-400 text-xs">{error}</div>}
        <div className="space-y-4 py-2">
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Client *</label>
            <Select value={form.client_id} onValueChange={(v) => set('client_id', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="task-client"><SelectValue placeholder="Select Client" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">{clients.map(c => <SelectItem key={c.client_id} value={c.client_id}>{c.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Assigned To *</label>
            <Select value={form.assigned_to} onValueChange={(v) => set('assigned_to', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="task-employee"><SelectValue placeholder="Select Employee" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 max-h-[250px] z-[200]">{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Task Date *</label>
              <input
                type="date"
                value={form.task_date}
                onChange={(e) => set('task_date', e.target.value)}
                className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-indigo-500"
                data-testid="task-date"
              />
            </div>
            <div>
              <label className="text-xs text-slate-500 mb-1 block">Priority</label>
              <Select value={form.priority} onValueChange={(v) => set('priority', v)}>
                <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="task-priority"><SelectValue placeholder="Optional" /></SelectTrigger>
                <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]"><SelectItem value="High">High</SelectItem><SelectItem value="Medium">Medium</SelectItem><SelectItem value="Low">Low</SelectItem></SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Task Type *</label>
            <Select value={form.task_type} onValueChange={(v) => set('task_type', v)}>
              <SelectTrigger className="bg-black/40 border-white/10 text-white" data-testid="task-type"><SelectValue placeholder="Type" /></SelectTrigger>
              <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                <SelectItem value="Short-form Video">Short-form Video</SelectItem>
                <SelectItem value="Long-form Video">Long-form Video</SelectItem>
                <SelectItem value="Static">Static</SelectItem>
                <SelectItem value="Carousel">Carousel</SelectItem>
                <SelectItem value="Motion Graphic">Motion Graphic</SelectItem>
                <SelectItem value="Grid">Grid</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Raw File Link</label>
            <Input value={form.raw_file_link} onChange={(e) => set('raw_file_link', e.target.value)} placeholder="https://drive.google.com/..." className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="task-raw-link" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Script</label>
            <Input value={form.script} onChange={(e) => set('script', e.target.value)} placeholder="Script link or text" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="task-script" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Reference Link</label>
            <Input value={form.reference_link} onChange={(e) => set('reference_link', e.target.value)} placeholder="Reference link" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="task-ref-link" />
          </div>
          <div>
            <label className="text-xs text-slate-500 mb-1 block">Notes</label>
            <Input value={form.notes} onChange={(e) => set('notes', e.target.value)} placeholder="Additional notes" className="bg-black/40 border-white/10 text-white h-9 text-sm" data-testid="task-notes" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>
          <Button disabled={loading || !form.client_id || !form.assigned_to || !form.task_type} onClick={handleSubmit} className="bg-indigo-600 hover:bg-indigo-700" data-testid="submit-task">{loading ? 'Creating...' : 'Create Task'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== CSV UPLOAD DIALOG (with Excel template & dropdown mapping) ==========
function CSVUploadDialog({ clients, employees, onClose, onDone }) {
  const [step, setStep] = useState('upload'); // upload | map | result
  const [mappedRows, setMappedRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const fileRef = useRef(null);

  const TASK_TYPES = ['Short-form Video', 'Long-form Video', 'Static', 'Carousel', 'Motion Graphic', 'Grid'];
  const PRIORITIES = ['High', 'Medium', 'Low'];

  const parseCSV = (text) => {
    const lines = text.trim().split('\n');
    if (lines.length < 2) return [];
    const headers = lines[0].split(',').map(h => h.trim().toLowerCase().replace(/\s+/g, '_'));
    return lines.slice(1).filter(l => l.trim()).map(line => {
      const vals = line.split(',').map(v => v.trim());
      const row = {};
      headers.forEach((h, i) => { row[h] = vals[i] || ''; });
      return row;
    });
  };

  const parseXLSX = async (buffer) => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    await wb.xlsx.load(buffer);
    const ws = wb.getWorksheet('Tasks') || wb.worksheets[0];
    if (!ws) return [];
    const headers = [];
    ws.getRow(1).eachCell((cell, colNum) => {
      headers[colNum] = String(cell.value || '').trim().toLowerCase().replace(/\s+/g, '_');
    });
    const rows = [];
    ws.eachRow((row, rowNum) => {
      if (rowNum === 1) return;
      const obj = {};
      let hasData = false;
      row.eachCell((cell, colNum) => {
        const key = headers[colNum];
        if (key) {
          let val = '';
          if (cell.value instanceof Date) {
            // Format Date objects as YYYY-MM-DD
            const d = cell.value;
            val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else if (typeof cell.value === 'number' && key === 'task_date') {
            // Excel serial date number — convert to date
            const d = new Date(Math.round((cell.value - 25569) * 86400 * 1000));
            val = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
          } else if (cell.value != null) {
            val = String(cell.value).trim();
            // Clean timezone from date strings like "2026-04-15T00:00:00.000Z" or "Mon Apr 15 2026..."
            if (key === 'task_date' && val.length > 10) {
              const match = val.match(/(\d{4})-(\d{2})-(\d{2})/);
              if (match) val = match[0];
              else {
                const parsed = new Date(val);
                if (!isNaN(parsed)) val = `${parsed.getFullYear()}-${String(parsed.getMonth() + 1).padStart(2, '0')}-${String(parsed.getDate()).padStart(2, '0')}`;
              }
            }
          }
          obj[key] = val;
          if (val) hasData = true;
        }
      });
      if (hasData) rows.push(obj);
    });
    return rows;
  };

  const mapRows = (rows) => {
    return rows.map(row => {
      const clientMatch = clients.find(c => c.name.toLowerCase() === (row.client_name || '').toLowerCase());
      const empMatch = employees.find(e => e.name.toLowerCase() === (row.assigned_to || '').toLowerCase());
      return {
        ...row,
        _client_id: clientMatch?.client_id || '',
        _client_display: clientMatch?.name || '',
        _emp_id: empMatch?.employee_id || '',
        _emp_display: empMatch?.name || '',
        _valid: !!(clientMatch && empMatch),
      };
    });
  };

  const handleFile = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const isXlsx = file.name.endsWith('.xlsx');

    if (isXlsx) {
      const buffer = await file.arrayBuffer();
      const rows = await parseXLSX(buffer);
      if (rows.length === 0) { toast.error('No data rows found'); return; }
      setMappedRows(mapRows(rows));
      setStep('map');
    } else {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const rows = parseCSV(ev.target.result);
        if (rows.length === 0) { toast.error('No data rows found'); return; }
        setMappedRows(mapRows(rows));
        setStep('map');
      };
      reader.readAsText(file);
    }
  };

  const updateRowMapping = (idx, field, value) => {
    setMappedRows(prev => {
      const updated = [...prev];
      const row = { ...updated[idx] };
      if (field === 'client') {
        const c = clients.find(cl => cl.client_id === value);
        row._client_id = value;
        row._client_display = c?.name || '';
      } else {
        const emp = employees.find(e => e.employee_id === value);
        row._emp_id = value;
        row._emp_display = emp?.name || '';
      }
      row._valid = !!(row._client_id && row._emp_id);
      updated[idx] = row;
      return updated;
    });
  };

  const validRows = mappedRows.filter(r => r._valid);

  const handleUpload = async () => {
    setLoading(true);
    try {
      const rows = validRows.map(r => ({
        client_name: r._client_display,
        assigned_to: r._emp_display,
        task_type: r.task_type || '',
        task_date: r.task_date || '',
        priority: r.priority || '',
        raw_file_link: r.raw_file_link || '',
        script: r.script || '',
        reference_link: r.reference_link || '',
        notes: r.notes || '',
      }));
      const { data } = await axios.post(`${API}/production/csv-upload`, { rows }, { withCredentials: true });
      setResult(data);
      setStep('result');
      if (data.created > 0) toast.success(`${data.created} tasks created`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Upload failed'); }
    finally { setLoading(false); }
  };

  const downloadExcelTemplate = async () => {
    const ExcelJS = (await import('exceljs')).default;
    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet('Tasks');

    // Headers
    const headers = ['Client Name', 'Assigned To', 'Task Type', 'Task Date', 'Priority', 'Raw File Link', 'Script', 'Reference Link', 'Notes'];
    const headerRow = ws.addRow(headers);
    headerRow.eachCell(cell => {
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' } };
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF4338CA' } };
      cell.alignment = { horizontal: 'center' };
    });

    // Column widths
    ws.columns = [
      { width: 20 }, { width: 20 }, { width: 22 }, { width: 14 }, { width: 12 },
      { width: 30 }, { width: 25 }, { width: 30 }, { width: 30 },
    ];

    // Build dropdown lists
    const clientNames = clients.map(c => c.name);
    const empNames = employees.map(e => e.name);
    const taskTypeList = `"${TASK_TYPES.join(',')}"`;
    const priorityList = `"${PRIORITIES.join(',')}"`;

    // Add a hidden "Lists" sheet for long dropdown lists
    const listsSheet = wb.addWorksheet('_Lists');
    listsSheet.state = 'veryHidden';
    clientNames.forEach((name, i) => { listsSheet.getCell(`A${i + 1}`).value = name; });
    empNames.forEach((name, i) => { listsSheet.getCell(`B${i + 1}`).value = name; });

    const clientRange = clientNames.length > 0 ? `_Lists!$A$1:$A$${clientNames.length}` : null;
    const empRange = empNames.length > 0 ? `_Lists!$B$1:$B$${empNames.length}` : null;

    // Apply data validations for 100 rows
    const maxRow = 101;
    for (let r = 2; r <= maxRow; r++) {
      // Client Name dropdown (column A)
      if (clientRange) {
        ws.getCell(`A${r}`).dataValidation = {
          type: 'list', allowBlank: true, formulae: [clientRange],
          showErrorMessage: true, errorTitle: 'Invalid Client', error: 'Please select a client from the dropdown.',
        };
      }
      // Assigned To dropdown (column B)
      if (empRange) {
        ws.getCell(`B${r}`).dataValidation = {
          type: 'list', allowBlank: true, formulae: [empRange],
          showErrorMessage: true, errorTitle: 'Invalid Employee', error: 'Please select an employee from the dropdown.',
        };
      }
      // Task Type dropdown (column C)
      ws.getCell(`C${r}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [taskTypeList],
        showErrorMessage: true, errorTitle: 'Invalid Type', error: 'Please select a valid task type.',
      };
      // Priority dropdown (column E)
      ws.getCell(`E${r}`).dataValidation = {
        type: 'list', allowBlank: true, formulae: [priorityList],
        showErrorMessage: true, errorTitle: 'Invalid Priority', error: 'Please select High, Medium, or Low.',
      };
      // Task Date as plain text (column D) — prevents timezone interpretation
      ws.getCell(`D${r}`).numFmt = '@';
    }

    // Add example row — date as plain string, not Date object
    const today = new Date().toISOString().split('T')[0];
    const exRow = ws.getRow(2);
    exRow.getCell(1).value = clientNames[0] || 'ClientName';
    exRow.getCell(2).value = empNames[0] || 'EmployeeName';
    exRow.getCell(3).value = 'Short-form Video';
    exRow.getCell(4).value = today;
    exRow.getCell(4).numFmt = '@';
    exRow.getCell(5).value = 'High';
    exRow.getCell(9).value = 'Example task';
    exRow.eachCell(cell => { cell.font = { italic: true, color: { argb: 'FF888888' } }; });

    const buffer = await wb.xlsx.writeBuffer();
    const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'production_tasks_template.xlsx';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <Dialog open={true} onOpenChange={onClose}>
      <DialogContent className="max-w-3xl bg-[#0a0a0f] border-white/10 max-h-[85vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="text-white font-heading text-lg">Bulk Upload Tasks</DialogTitle></DialogHeader>

        {step === 'upload' && (
          <div className="space-y-4 py-2">
            <div>
              <label className="text-xs text-slate-400 mb-2 block font-semibold uppercase tracking-widest">Step 1: Download Template</label>
              <p className="text-[11px] text-slate-500 mb-2">Download the Excel template with dropdown menus for Client, Employee, Task Type, and Priority. Fill it in and upload below.</p>
              <Button variant="outline" onClick={downloadExcelTemplate} className="bg-white/5 border-white/10 text-slate-300 text-xs" data-testid="download-template">
                <Upload size={14} className="mr-1 rotate-180" /> Download Excel Template (.xlsx)
              </Button>
            </div>
            <div className="border-t border-white/5 pt-4">
              <label className="text-xs text-slate-400 mb-2 block font-semibold uppercase tracking-widest">Step 2: Upload Filled File</label>
              <input type="file" accept=".csv,.xlsx" onChange={handleFile} ref={fileRef} className="hidden" />
              <Button variant="outline" onClick={() => fileRef.current.click()} className="bg-white/5 border-white/10 text-slate-300 text-xs" data-testid="csv-file-btn">
                <Upload size={14} className="mr-1" /> Choose File (.xlsx or .csv)
              </Button>
              <p className="text-[10px] text-slate-500 mt-1">After uploading, you'll review and confirm the data mapping before tasks are created.</p>
            </div>
          </div>
        )}

        {step === 'map' && (
          <div className="space-y-4 py-2">
            <div className="flex items-center gap-2 mb-2">
              <span className="text-xs text-slate-400">{mappedRows.length} rows parsed</span>
              <span className="text-[10px] px-2 py-0.5 rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">{validRows.length} valid</span>
              {mappedRows.length - validRows.length > 0 && (
                <span className="text-[10px] px-2 py-0.5 rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">{mappedRows.length - validRows.length} need mapping</span>
              )}
            </div>
            <div className="border border-white/10 rounded overflow-x-auto max-h-[400px]">
              <table className="data-table text-xs w-full">
                <thead>
                  <tr>
                    <th className="w-8">#</th>
                    <th>Client</th>
                    <th>Employee</th>
                    <th>Type</th>
                    <th>Date</th>
                    <th>Priority</th>
                    <th className="w-12">OK</th>
                  </tr>
                </thead>
                <tbody>
                  {mappedRows.map((row, i) => (
                    <tr key={i} className={row._valid ? '' : 'bg-rose-500/5'} data-testid={`csv-row-${i}`}>
                      <td className="text-slate-500">{i + 1}</td>
                      <td>
                        <select
                          value={row._client_id}
                          onChange={(e) => updateRowMapping(i, 'client', e.target.value)}
                          className={`bg-black/40 border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-indigo-500 ${row._client_id ? 'border-emerald-500/30 text-white' : 'border-rose-500/30 text-rose-400'}`}
                          data-testid={`csv-client-${i}`}
                        >
                          <option value="">— Select Client —</option>
                          {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                        </select>
                        {!row._client_id && row.client_name && <span className="text-[9px] text-rose-400/60 block mt-0.5">CSV: "{row.client_name}"</span>}
                      </td>
                      <td>
                        <select
                          value={row._emp_id}
                          onChange={(e) => updateRowMapping(i, 'employee', e.target.value)}
                          className={`bg-black/40 border rounded px-2 py-1 text-xs w-full focus:outline-none focus:ring-1 focus:ring-indigo-500 ${row._emp_id ? 'border-emerald-500/30 text-white' : 'border-rose-500/30 text-rose-400'}`}
                          data-testid={`csv-employee-${i}`}
                        >
                          <option value="">— Select Employee —</option>
                          {employees.map(e => <option key={e.employee_id} value={e.employee_id}>{e.name}</option>)}
                        </select>
                        {!row._emp_id && row.assigned_to && <span className="text-[9px] text-rose-400/60 block mt-0.5">CSV: "{row.assigned_to}"</span>}
                      </td>
                      <td className="text-slate-300">{row.task_type || '—'}</td>
                      <td className="text-slate-400">{row.task_date || '—'}</td>
                      <td className="text-slate-400">{row.priority || '—'}</td>
                      <td className="text-center">
                        {row._valid ? (
                          <CheckCircle2 size={14} className="text-emerald-400 mx-auto" />
                        ) : (
                          <X size={14} className="text-rose-400 mx-auto" />
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {step === 'result' && result && (
          <div className="space-y-3 py-2">
            <div className="bg-emerald-500/10 border border-emerald-500/20 rounded p-3 text-xs">
              <p className="text-emerald-400 font-semibold">Created {result.created} tasks</p>
            </div>
            {result.errors?.length > 0 && (
              <div className="bg-rose-500/10 border border-rose-500/20 rounded p-3 text-xs space-y-1">
                {result.errors.map((e, i) => <p key={i} className="text-rose-400">Row {e.row}: {e.error}</p>)}
              </div>
            )}
          </div>
        )}

        <DialogFooter>
          {step === 'upload' && <Button variant="outline" onClick={onClose} className="bg-white/5 border-white/10 text-white">Cancel</Button>}
          {step === 'map' && (
            <>
              <Button variant="outline" onClick={() => { setStep('upload'); setMappedRows([]); }} className="bg-white/5 border-white/10 text-white">Back</Button>
              <Button
                disabled={loading || validRows.length === 0}
                onClick={handleUpload}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="csv-confirm-upload"
              >
                {loading ? 'Uploading...' : `Upload ${validRows.length} Valid Tasks`}
              </Button>
            </>
          )}
          {step === 'result' && <Button onClick={onDone} className="bg-emerald-600 hover:bg-emerald-700" data-testid="csv-done-btn">Done</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ========== TASK DETAIL SHEET ==========
function TaskDetailSheet({ task, canWrite, canExecute, isAdmin, user, onClose, onAction, onToggleSpecial, onUpdate }) {
  const [showComplete, setShowComplete] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [finalLink, setFinalLink] = useState('');
  const [loading, setLoading] = useState(false);
  const [editForm, setEditForm] = useState({
    task_type: task.task_type || '', priority: task.priority || '',
    raw_file_link: task.raw_file_link || '', script: task.script || '',
    reference_link: task.reference_link || '', notes: task.notes || '',
    task_date: task.task_date || '',
  });
  const isOwner = isAdmin || task.assigned_to === user?.employee_id;

  const handleComplete = async () => {
    setLoading(true);
    try {
      await onAction(task.task_id, 'complete', { final_drive_link: finalLink });
      onUpdate();
    } finally { setLoading(false); }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/production/tasks/${task.task_id}`, { withCredentials: true });
      toast.success('Task deleted');
      onUpdate();
    } catch (err) { toast.error('Failed'); }
  };

  const handleCarryForward = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/production/tasks/${task.task_id}/carry-forward`, {}, { withCredentials: true });
      toast.success(`Carried forward to ${data.new_date} as ${data.new_task_id}`);
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed'); }
    finally { setLoading(false); }
  };

  const handleEditSave = async () => {
    setLoading(true);
    try {
      const payload = {};
      if (editForm.task_type && editForm.task_type !== task.task_type) payload.task_type = editForm.task_type;
      if (editForm.priority !== (task.priority || '')) payload.priority = editForm.priority || null;
      if (editForm.task_date !== (task.task_date || '')) payload.task_date = editForm.task_date;
      if (editForm.raw_file_link !== (task.raw_file_link || '')) payload.raw_file_link = editForm.raw_file_link;
      if (editForm.script !== (task.script || '')) payload.script = editForm.script;
      if (editForm.reference_link !== (task.reference_link || '')) payload.reference_link = editForm.reference_link;
      if (editForm.notes !== (task.notes || '')) payload.notes = editForm.notes;
      if (Object.keys(payload).length === 0) { setShowEdit(false); return; }
      await axios.put(`${API}/production/tasks/${task.task_id}`, payload, { withCredentials: true });
      toast.success('Task updated');
      onUpdate();
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to update'); }
    finally { setLoading(false); }
  };

  return (
    <Sheet open={true} onOpenChange={onClose}>
      <SheetContent className="w-full sm:max-w-lg bg-[#0a0a0f] border-l border-white/10 overflow-y-auto">
        <SheetHeader className="mb-6">
          <div className="flex items-center gap-2 mb-1">
            <span className={`text-[10px] px-2 py-0.5 rounded border ${STATUS_COLORS[task.status]}`}>{task.status}</span>
            {task.special_production && <Star size={14} className="text-amber-400" />}
          </div>
          <SheetTitle className="text-white font-heading text-xl" data-testid="task-detail-title">{task.task_type} — {task.client_name}</SheetTitle>
        </SheetHeader>

        <div className="space-y-4">
          <div className="glass-card p-4">
            <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Details</h3>
            <div className="space-y-2 text-xs">
              <div className="flex justify-between"><span className="text-slate-500">Client</span><span className="text-white">{task.client_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Assigned To</span><span className="text-white">{task.assigned_name}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Date</span><span className="text-white">{task.task_date || '—'}</span></div>
              <div className="flex justify-between"><span className="text-slate-500">Type</span><span className="flex items-center gap-1">{TYPE_ICONS[task.task_type]}{task.task_type}</span></div>
              {task.priority && <div className="flex justify-between"><span className="text-slate-500">Priority</span><span className={`px-2 py-0.5 rounded ${PRIORITY_COLORS[task.priority]}`}>{task.priority}</span></div>}
              <div className="flex justify-between"><span className="text-slate-500">Total Time</span><span className="text-cyan-400 font-mono">{task.total_time}</span></div>
              {task.previous_time_secs > 0 && (
                <div className="flex justify-between"><span className="text-slate-500">Today's Time</span><span className="text-slate-400 font-mono">{task.session_time}</span></div>
              )}
              <div className="flex justify-between"><span className="text-slate-500">Production Score</span><span className="text-amber-400 font-bold">{task.production_value}</span></div>
            </div>
          </div>

          {(task.raw_file_link || task.script || task.reference_link || task.notes) && (
            <div className="glass-card p-4">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Inputs</h3>
              <div className="space-y-2 text-xs">
                {task.raw_file_link && <div><span className="text-slate-500 block">Raw File</span><a {...externalLinkProps(task.raw_file_link)} className="text-cyan-400 hover:text-cyan-300 break-all text-left underline underline-offset-2 block" data-testid="task-raw-link-open">{task.raw_file_link}</a></div>}
                {task.script && <div><span className="text-slate-500 block">Script</span>{task.script.startsWith('http') || /^[\w-]+(\.[\w-]+)+/.test(task.script.trim()) ? <a {...externalLinkProps(task.script)} className="text-cyan-400 hover:text-cyan-300 break-all text-left underline underline-offset-2 block">{task.script}</a> : <span className="text-slate-300">{task.script}</span>}</div>}
                {task.reference_link && <div><span className="text-slate-500 block">Reference</span><a {...externalLinkProps(task.reference_link)} className="text-cyan-400 hover:text-cyan-300 break-all text-left underline underline-offset-2 block" data-testid="task-ref-link-open">{task.reference_link}</a></div>}
                {task.notes && <div><span className="text-slate-500 block">Notes</span><span className="text-slate-300">{task.notes}</span></div>}
              </div>
            </div>
          )}

          {/* Edit Task */}
          {canExecute && !showEdit && task.status !== 'Completed' && (
            <Button onClick={() => setShowEdit(true)} variant="outline" className="w-full bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="edit-task-btn"><Edit2 size={14} className="mr-1" /> Edit Task</Button>
          )}
          {showEdit && (
            <div className="glass-card p-4 space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-indigo-400">Edit Task</h3>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Task Type</label>
                  <Select value={editForm.task_type} onValueChange={(v) => setEditForm(p => ({ ...p, task_type: v }))}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue /></SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                      <SelectItem value="Short-form Video">Short-form Video</SelectItem>
                      <SelectItem value="Long-form Video">Long-form Video</SelectItem>
                      <SelectItem value="Static">Static</SelectItem>
                      <SelectItem value="Carousel">Carousel</SelectItem>
                      <SelectItem value="Motion Graphic">Motion Graphic</SelectItem>
                      <SelectItem value="Grid">Grid</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <label className="text-[10px] text-slate-500 block mb-1">Priority</label>
                  <Select value={editForm.priority} onValueChange={(v) => setEditForm(p => ({ ...p, priority: v }))}>
                    <SelectTrigger className="bg-black/40 border-white/10 text-white h-8 text-xs"><SelectValue placeholder="None" /></SelectTrigger>
                    <SelectContent className="bg-[#0f0f15] border-white/10 z-[200]">
                      <SelectItem value="High">High</SelectItem>
                      <SelectItem value="Medium">Medium</SelectItem>
                      <SelectItem value="Low">Low</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Task Date</label>
                <input type="date" value={editForm.task_date} onChange={(e) => setEditForm(p => ({ ...p, task_date: e.target.value }))} className="w-full bg-black/40 border border-white/10 text-white rounded-md px-3 py-1.5 text-xs focus:outline-none focus:ring-1 focus:ring-indigo-500" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Raw File Link</label>
                <Input value={editForm.raw_file_link} onChange={(e) => setEditForm(p => ({ ...p, raw_file_link: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Script</label>
                <Input value={editForm.script} onChange={(e) => setEditForm(p => ({ ...p, script: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Reference Link</label>
                <Input value={editForm.reference_link} onChange={(e) => setEditForm(p => ({ ...p, reference_link: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" />
              </div>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Notes</label>
                <Input value={editForm.notes} onChange={(e) => setEditForm(p => ({ ...p, notes: e.target.value }))} className="bg-black/40 border-white/10 text-white h-8 text-xs" />
              </div>
              <div className="flex gap-2">
                <Button variant="outline" onClick={() => setShowEdit(false)} className="flex-1 bg-white/5 border-white/10 text-white h-8 text-xs">Cancel</Button>
                <Button disabled={loading} onClick={handleEditSave} className="flex-1 bg-indigo-600 hover:bg-indigo-700 h-8 text-xs" data-testid="save-edit-task">{loading ? 'Saving...' : 'Save Changes'}</Button>
              </div>
            </div>
          )}

          {task.final_drive_link && (
            <div className="glass-card p-4 border-emerald-500/10">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400 mb-2">Output</h3>
              <a {...externalLinkProps(task.final_drive_link)} className="text-cyan-400 hover:text-cyan-300 text-sm break-all text-left underline underline-offset-2 block" data-testid="final-drive-link-open">{task.final_drive_link}</a>
            </div>
          )}

          {/* Carry Forward Info */}
          {task.carried_forward_from && (
            <div className="text-[10px] px-3 py-2 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
              Carried forward from <span className="font-mono font-bold">{task.carried_forward_from}</span>
              {task.previous_time_secs > 0 && <span> (prev: {formatSecs(task.previous_time_secs)})</span>}
            </div>
          )}
          {task.carried_forward_to && (
            <div className="text-[10px] px-3 py-2 rounded bg-indigo-500/10 border border-indigo-500/20 text-indigo-400">
              Carried forward to <span className="font-mono font-bold">{task.carried_forward_to}</span>
            </div>
          )}

          {/* Actions */}
          {canExecute && isOwner && task.status === 'Pending' && (
            <Button onClick={() => onAction(task.task_id, 'start')} className="w-full bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="detail-start"><Play size={14} className="mr-1" /> Start Task</Button>
          )}
          {canExecute && isOwner && task.status === 'Paused' && (
            <div className="flex gap-2">
              <Button onClick={() => onAction(task.task_id, 'start')} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="detail-resume"><Play size={14} className="mr-1" /> Resume</Button>
              <Button onClick={() => setShowComplete(true)} className="flex-1 bg-cyan-600 hover:bg-cyan-700 h-9 text-xs" data-testid="detail-complete-btn"><CheckCircle2 size={14} className="mr-1" /> Complete</Button>
            </div>
          )}
          {canExecute && isOwner && task.status === 'In Progress' && (
            <div className="flex gap-2">
              <Button onClick={() => onAction(task.task_id, 'pause')} variant="outline" className="flex-1 bg-white/5 border-white/10 text-slate-300 h-9 text-xs" data-testid="detail-pause"><Pause size={14} className="mr-1" /> Pause</Button>
              <Button onClick={() => setShowComplete(true)} className="flex-1 bg-emerald-600 hover:bg-emerald-700 h-9 text-xs" data-testid="detail-complete-btn"><CheckCircle2 size={14} className="mr-1" /> Complete</Button>
            </div>
          )}

          {/* Carry Forward — for incomplete tasks (admin or executing owner) */}
          {canExecute && (isAdmin || isOwner) && ['Pending', 'In Progress', 'Paused'].includes(task.status) && !task.carried_forward_to && (
            <Button onClick={handleCarryForward} disabled={loading} variant="outline" className="w-full bg-indigo-500/10 border-indigo-500/20 text-indigo-400 hover:bg-indigo-500/20 h-9 text-xs" data-testid="carry-forward-btn">
              <ArrowRight size={14} className="mr-1" /> {loading ? 'Carrying Forward...' : 'Carry Forward to Tomorrow'}
            </Button>
          )}

          {showComplete && (
            <div className="glass-card p-4 space-y-3">
              <h3 className="text-xs font-semibold tracking-widest uppercase text-emerald-400">Complete Task</h3>
              <div>
                <label className="text-[10px] text-slate-500 block mb-1">Final Drive Link *</label>
                <Input value={finalLink} onChange={(e) => setFinalLink(e.target.value)} placeholder="https://drive.google.com/final..." className="bg-black/40 border-white/10 text-white h-8 text-xs" data-testid="final-drive-link" />
              </div>
              <Button disabled={loading || !finalLink} onClick={handleComplete} className="w-full bg-emerald-600 hover:bg-emerald-700 h-8 text-xs" data-testid="confirm-complete">{loading ? 'Completing...' : 'Confirm Complete'}</Button>
            </div>
          )}

          {task.status === 'Completed' && isAdmin && (
            <Button onClick={() => onToggleSpecial(task.task_id)} variant="outline" className={`w-full h-9 text-xs ${task.special_production ? 'bg-amber-500/10 border-amber-500/20 text-amber-400' : 'bg-white/5 border-white/10 text-slate-300'}`} data-testid="toggle-special">
              <Star size={14} className="mr-1" /> {task.special_production ? 'Remove Special Production (2x)' : 'Mark as Special Production (2x)'}
            </Button>
          )}

          {canExecute && (
            <Button onClick={handleDelete} variant="outline" className="w-full bg-rose-500/10 border-rose-500/20 text-rose-400 h-9 text-xs" data-testid="delete-task"><X size={14} className="mr-1" /> Delete Task</Button>
          )}
        </div>
      </SheetContent>
    </Sheet>
  );
}
